import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {
    ComponentDesc,
    IPropsValues
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {Repeater, RepeaterLevel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {trackChangesProxy} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/TrackChangesProxy';
import {EventPropName} from '@mintdata/mint-kit/api/events/EventType';
import {IComponentLogic} from '@mintdata/mint-kit/api/events/logic/IComponentLogic';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {BindPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {ObjectValidationResult, ValidationResultBuilder} from '@mintdata/mint-kit/api/properties/ValidationResult';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {FrameMetaKey, FrameMetaValue, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {FrameMeta} from '@mintdata/mint-kit/api/values/frames/FrameMeta';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {StringUtil} from '@mintdata/mint-kit/impl/util/StringUtil';
import bind from 'bind-decorator';

interface RepeaterFrame
{
    itemIndex:ANumber | AEmpty;
    parentRepeater:IFrame;
}

export class LogicBridge
{
    private _componentDesc:ComponentDesc;

    private readonly _componentLogic:IComponentLogic<any> | null;

    public constructor(componentDesc:ComponentDesc)
    {
        this._componentDesc = componentDesc;

        const logicCtor = componentDesc.componentConfig.logic;
        this._componentLogic = logicCtor ? new componentDesc.componentConfig.logic() : null;
    }

    public dispose()
    {
        this._componentDesc = null;
    }

    @bind
    public validate(bindPath:BindPath, props:any):ObjectValidationResult
    {
        if( this._componentLogic )
            return this._componentLogic.validate(bindPath, props);

        return new ValidationResultBuilder().valid(bindPath).result();
    }

    @bind
    public populateEventHandlers(repeater:Repeater, propsValues:IPropsValues):IPropsValues
    {
        const eventHandlers = {};
        this._componentDesc.eventPropsMapByName.forEach((eventProp:EventProp) =>
        {
            eventHandlers[eventProp.name] = (event) =>
                this.handleEvent(eventProp, repeater, propsValues, event);
        });

        return Object.assign(propsValues, eventHandlers);
    }

    @bind
    public handleEvent(eventProp:EventProp, repeater:Repeater, propsValues:IPropsValues, event:any):void
    {
        event = this._handleComponentLogic(eventProp, repeater, propsValues, event);

        this._fireEventFrame(eventProp, repeater, event);
    };

    @bind
    private _handleComponentLogic(eventProp:EventProp, repeater:Repeater, propsValues:IPropsValues, event:any)
    {
        // TODO: handle array modifications properly - i.e. teach TrackChangesProxy to track array's splice/push/shift/unshift
        //  it's considered minor feature, because it was only needed once in TableLogic and allways easily replacable by treating array as immutable
        //  i.e. instead of `model.prop.push(e)` we can use `model.prop = [...model.prop, e]`

        // Possible changes cases ( "[..]" denotes bound part ) :
        //   1. Simple case - bound and changed paths are the same (suffixLength == 0)
        //    model.some[prop]
        //    model.items[0]
        //    model.items.0[prop]
        //
        //   2. Bound primitive array's item changed (suffixLength == 1 && lastPart is a number)
        //    model[items].0
        //
        //   3. Bound composite's sub-prop changed (suffixLength == 1 && lastPart is a string)
        //    model[some].prop
        //
        //   4. Bound composite array's item's sub-prop changed (suffixLength == 2)
        //    model[items].0.prop

        const {bindings} = commonControllers;

        if( this._componentLogic )
        {
            const propsTracking = trackChangesProxy(propsValues);
            event = this._componentLogic.onEvent(eventProp.name, event, propsTracking.proxy()) || event;
            propsTracking.revoke();

            const changes = propsTracking.changes();
            for( const change of changes )
            {
                mk.debug(area.Events, `Change from event: ${change.path.asKey()} => `, change.newValue);

                const {path, newValue} = change;
                Assert.yes(path.length() > 0);

                const propName = path.parts[0] as string;

                const prop = this._componentDesc.properties.get(propName);
                const propPath = path.shift();

                let boundPath = propPath;
                let suffixLength = 0;
                let propBinding = null;
                while( suffixLength < 3 )
                {
                    propBinding = prop.getBinding(boundPath);
                    if( propBinding || boundPath.length() === 0 )
                        break;

                    boundPath = boundPath.pop();
                    ++suffixLength;
                }

                if( propBinding )
                {
                    if( suffixLength === 0 )
                    {
                        //   1. Simple case - bound and changed paths are the same (suffixLength == 0)
                        bindings.updateBoundSlotValue(propBinding, repeater, newValue, null, null);
                        continue;
                    }

                    const lastPartN = propPath.getNumberPartAt(-1);
                    if( suffixLength === 1 && !isNaN(lastPartN) )
                    {
                        //   2. Bound primitive array's item changed (suffixLength == 1 && lastPart is a number)
                        bindings.updateBoundSlotValue(propBinding, repeater, newValue, lastPartN, null);
                        continue;
                    }

                    const lastPart = propPath.getStringPartAt(-1);
                    if( suffixLength === 1 && isNaN(lastPartN) )
                    {
                        //   3. Bound composite's sub-prop changed (suffixLength == 1 && lastPart is a string)
                        bindings.updateBoundSlotValue(propBinding, repeater, newValue, null, lastPart);
                        continue;
                    }

                    const preLastPartN = propPath.getNumberPartAt(-2);
                    if( suffixLength === 2 && isNaN(lastPartN) && !isNaN(preLastPartN) )
                    {
                        //   4. Bound composite array's item's sub-prop changed (suffixLength == 2)
                        bindings.updateBoundSlotValue(propBinding, repeater, newValue, preLastPartN, lastPart);
                        continue;
                    }
                }

                // not bound => just change inner prop's value
                prop.setPropValue(propPath, newValue);
            }
            this._componentDesc.propChanged({} as any);
        }

        return event;
    }

    private _fireEventFrame(eventProp:EventProp, repeater:Repeater, event:any)
    {
        const {bindings} = commonControllers;

        if( eventProp.isEnabled && eventProp.bindingId )
        {
            const eventBinding = bindings.getEventBinding(eventProp.bindingId);
            if( !eventBinding )
                return;

            const eventFrame = LogicBridge.getEventFrame(
                eventProp.name,
                event,
                this._componentDesc.name.value,
                this._componentDesc.componentConfig.name,
                this._componentDesc.box.id,
                repeater
            );

            bindings.reEvaluateLazySlot(eventBinding, repeater, eventFrame);
        }
    }

    public static getEventFrame(type:EventPropName,
                                event:any,
                                componentName:string,
                                componentType:string,
                                componentId:string,
                                repeater?:Repeater):IFrame
    {
        const eventType:string = LogicBridge._getEventType(type);
        const fields:Array<FrameField> = [
            new FrameField(LogicBridge.UI_EVENT_COLUMN, Type.String),
            new FrameField(LogicBridge.COMPONENT_NAME_COLUMN, Type.String),
            new FrameField(LogicBridge.COMPONENT_TYPE_COLUMN, Type.String)
        ];
        const firstRow:Array<IValue> = [
            AString.fromString(eventType),
            AString.fromString(componentName),
            AString.fromString(componentType)
        ];
        const data:Array<Array<IValue>> = [firstRow];
        const metadata:Map<FrameMetaKey, FrameMetaValue> = new Map<FrameMetaKey, FrameMetaValue>();

        metadata.set(LogicBridge.COMPONENT_ID_COLUMN, componentId);
        metadata.set(LogicBridge.COMPONENT_NAME_COLUMN, componentName);
        metadata.set(LogicBridge.COMPONENT_TYPE_COLUMN, componentType);
        metadata.set(LogicBridge.UI_EVENT_COLUMN, eventType);

        Object.keys(event).forEach((dataName:any) =>
        {
            const fieldData:any = event[dataName];
            const fieldName:string = StringUtil.camelCaseToLoDash(dataName);

            if( fieldData != null )
            {
                const isBaseType:boolean = Type.isInstanceOfIValue(fieldData);
                const dataType:Type = isBaseType
                    ? (fieldData as IValue).getType()
                    : Type.String;

                fields.push(new FrameField(fieldName, dataType));
                firstRow.push(
                    isBaseType
                        ? fieldData
                        : AString.fromString(fieldData.toString())
                );
            }
        });

        if( repeater )
        {
            const repeaterFrame:RepeaterFrame = LogicBridge._makeRepeaterFrame(repeater);

            fields.push(new FrameField('repeated_item_index', Type.Number));
            firstRow.push(repeaterFrame.itemIndex);

            fields.push(new FrameField('parent_repeater', Type.Frame));
            firstRow.push(repeaterFrame.parentRepeater);
        }

        const frame:IFrame = Frames.fromFieldsAndData(fields, data, new FrameMeta(metadata));

        mk.info(area.Events, `onEvent(${type}) -> Frame:`, frame.toJSON());

        return frame;
    }

    private static _getEventType(type:EventPropName):string
    {
        type = StringUtil.camelCaseToLoDash(
            type.replace(/^on(?=.+)/, '')
        );

        return type;
    }

    private static _makeRepeaterFrame(repeater:Repeater):RepeaterFrame
    {
        const frame:IFrame = repeater.levels.reduce((parent:any, current:RepeaterLevel) =>
        {
            if( !parent && !current.isRepeated ) return null;

            if( !current.isRepeated ) return parent;

            // // index is 1-based
            return LogicBridge._makeItemFrame(current.index + 1, parent);
        }, null as IFrame);

        return {
            itemIndex: frame
                ? frame.getCell('repeated_item_index', 0) as ANumber
                : AEmpty.Value,
            parentRepeater: frame
                ? frame.getCell('parent_repeater', 0) as IFrame
                : AFrame.empty()
        };
    }

    private static _makeItemFrame(index:number, parent:IFrame):IFrame
    {
        const fields:Array<FrameField> = [
            new FrameField('repeated_item_index', Type.Number),
            new FrameField('parent_repeater', Type.Frame)
        ];

        const firstRow:Array<IValue> = [
            ANumber.fromNumber(index),
            parent ? parent : AFrame.empty()
        ];

        const data:Array<Array<IValue>> = [firstRow];

        return Frames.fromFieldsAndData(fields, data);
    }

    public static COMPONENT_ID_COLUMN = 'component_id';
    public static COMPONENT_NAME_COLUMN = 'component_name';
    public static COMPONENT_TYPE_COLUMN = 'component_type';
    public static UI_EVENT_COLUMN = 'ui_event';
}
